/**
 * Password-recovery link plumbing that has to run BEFORE the Supabase client
 * exists.
 *
 * Supabase's `detectSessionInUrl` consumes the auth fragment on client init and
 * then `history.replaceState`s it away, so anything that wants to look at the
 * incoming link — to route on it, or to read the provider's error out of it —
 * has to look first. That is why `catchRecoveryLink()` is called at the top of
 * lib/supabase.ts rather than from a component, and why this module snapshots
 * the entry URL at import time.
 *
 * The project runs the default `flowType: 'implicit'` (supabase-js's own
 * DEFAULT_AUTH_OPTIONS), so a recovery link comes back as
 * `#access_token=…&refresh_token=…&type=recovery`. That `type` marker is what
 * makes the rescue below possible — and, unlike PKCE, it means the link also
 * works when the mail is opened on a different device than it was requested
 * from.
 */

/** Where the "set a new password" screen lives. */
export const RESET_PASSWORD_PATH = '/reset-password';

/** The URL the browser entered on, captured before Supabase can rewrite it. */
const INITIAL_HREF = typeof window === 'undefined' ? '' : window.location.href;

function paramsOf(url: URL): URLSearchParams {
  const hash = new URLSearchParams(url.hash.replace(/^#/, ''));
  const query = url.searchParams;
  // A value can arrive in either half depending on the flow and on whether
  // GoTrue answered with a session or an error; merge with the hash winning.
  const merged = new URLSearchParams(query);
  for (const [key, value] of hash) merged.set(key, value);
  return merged;
}

function parse(href: string): URL | null {
  try {
    return new URL(href);
  } catch {
    return null;
  }
}

/**
 * The path a recovery link should have landed on, or null when this URL is not
 * one.
 *
 * Recovery mail is sent with an explicit `redirectTo`, but GoTrue only honours
 * it when the exact URL is in the project's Redirect URLs allowlist — otherwise
 * it silently falls back to the Site URL and the student lands on the marketing
 * home page, signed in, with no way to set a password. This turns that dead end
 * back into the reset screen.
 */
export function recoveryRedirectTarget(href: string): string | null {
  const url = parse(href);
  if (url === null) return null;
  if (url.pathname === RESET_PASSWORD_PATH) return null;
  if (paramsOf(url).get('type') !== 'recovery') return null;
  return `${RESET_PASSWORD_PATH}${url.search}${url.hash}`;
}

const FRIENDLY_ERRORS = new Map<string, string>([
  [
    'otp_expired',
    'That password reset link has expired. Reset links are valid for a short time — request a new one below.',
  ],
  [
    'access_denied',
    'That password reset link is no longer valid. It may have already been used — request a new one below.',
  ],
]);

/**
 * The provider's own explanation for a link that did not produce a session.
 *
 * GoTrue reports these as `error` / `error_code` / `error_description` on the
 * redirect, never as a failed request, so without reading them here an expired
 * link is indistinguishable from a mistyped URL.
 */
export function readAuthLinkError(href: string): string | null {
  const url = parse(href);
  if (url === null) return null;
  const params = paramsOf(url);
  const code = params.get('error_code') ?? params.get('error');
  if (code === null) return null;
  return FRIENDLY_ERRORS.get(code) ?? params.get('error_description')?.replace(/\+/g, ' ') ?? null;
}

/** Same, for the URL this page was opened with. */
export function initialAuthLinkError(): string | null {
  return readAuthLinkError(INITIAL_HREF);
}

/**
 * Move a stray recovery link onto the reset route, fragment intact.
 *
 * Rewriting the path (rather than assigning `location`) keeps the tokens in the
 * fragment for the Supabase client to pick up on the very next tick — a real
 * navigation would drop them.
 */
export function catchRecoveryLink(): void {
  if (typeof window === 'undefined') return;
  const target = recoveryRedirectTarget(window.location.href);
  if (target === null) return;
  window.history.replaceState(window.history.state, '', target);
}
